import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, FormControl, Modal } from 'react-bootstrap';
import {
  BoxArrowUpRight,
  Clipboard,
  Envelope,
  EnvelopeOpen,
  Eye,
  EyeSlash,
  PersonCircle,
  PersonPlus,
  Telephone
} from 'react-bootstrap-icons';
import OverViewColumns from '../../../common/overview-columns/OverViewColumns';
import { customerFormFields, editCrmContactDetailsFields } from '../../../../helpers/overviewColumnsFields';
import { ENDPOINTS } from '../../../../helpers/constants';
import HorizontalProgress from '../../../common/HorizontalProgress';
import useDebouncedEffect from '../../../../hooks/useDebouncedEffect';
import { makeApiRequests } from '../../../../helpers/api';
import { toast } from 'react-toastify';
import { snakeCase } from 'lodash';
import useLocalization from '../../../../hooks/useLocalization';
import { validateEmail } from '../../../../helpers/global';

const CustomerActions = ({ translate, customerAction = [] }) => (
  <Dropdown className="d-inline-block mx-2">
    <Dropdown.Toggle
      variant="primary-light"
      className="rounded-circle m-0 flex items-center px-2 py-2.5"
      size="lg"
    ></Dropdown.Toggle>
    <Dropdown.Menu>
      {customerAction.map(({ onClick, text, Icon }) => (
        <Dropdown.Item key={text} onClick={onClick} className="mid">
          <Icon className="mx-2" />
          <span className="align-middle"> {translate(text)}</span>
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const MatchingCRMContact = ({ onViewCRMContactClick, contact = {}, translate }) => {
  return (
    <div className="p-2 border rounded mt-2 mx-2">
      <h6 className="smallFont fw-bold">{translate('matching_contact_found')}:</h6>
      <div onClick={() => onViewCRMContactClick(contact)} className="border hover p-2 bg-primary-light rounded">
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            {' '}
            <PersonCircle size={15} />{' '}
            <h6 className="mb-0 mx-1 mid fw-bold">
              {contact?.firstName} {contact?.lastName}
            </h6>
            <BoxArrowUpRight className="mx-1" size={15} />
          </div>
          <div style={{ fontSize: 10 }} className={`bg-dark px-2 py-0 text-white rounded`}>
            {translate(snakeCase(contact.status)) || contact.status}
          </div>
        </div>
        <div className="mt-1 d-flex">
          <div style={{ width: 20 }}>
            {' '}
            <Envelope size={15} />{' '}
          </div>

          <div className="mx-1 d-flex flex-wrap align-items-center">
            {' '}
            {contact?.emails?.map((email, index) => (
              <h6 key={email} className="mb-0 smallFont">
                {index !== 0 && ', '} {email}
              </h6>
            ))}
          </div>
        </div>{' '}
        <div className="mt-1 d-flex ">
          <div style={{ width: 20 }}>
            {' '}
            <Telephone size={15} />{' '}
          </div>
          <div className=" d-flex flex-wrap align-items-center">
            {contact?.phoneNumbers?.map((ph, index) => (
              <h6 key={ph} className="mb-0 smallFont">
                {index !== 0 && ', '} {ph}
              </h6>
            ))}
          </div>
        </div>{' '}
      </div>
    </div>
  );
};

const AddEditCustomerModal = ({
  show,
  onHide,
  user,
  users,
  onInviteClick,
  onSendInvitationLinkToCustomerClick,
  onCloneClick,
  onShowAllStrategiesClick,
  customer,
  showProgress,
  onChange,
  onCustomerSubmit,
  mode,
  onViewCRMContactClick,
  setParentCustomer,
  parentCustomer
}) => {
  const { translate, isRTL } = useLocalization();

  const [matchingCRMContact, setMatchingCRMContact] = useState(null);
  const [existingUsers, setExistingUsers] = useState([]);

  const crmContact = useMemo(() => customer?.crmContact, [customer]);

  const customerFormField = useMemo(() => customerFormFields(translate, user, users, crmContact), [
    translate,
    user,
    users,
    crmContact
  ]);

  const editCrmContactDetailsField = useMemo(() => editCrmContactDetailsFields(users, translate), [users, translate]);

  const getMatchingContactFromEmail = async email => {
    try {
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.FIND_CRM_CONTACT_FROM_EMAIL,
        requestBody: { email }
      });
      if (error) {
        toast.error(error);
        return;
      }
      setMatchingCRMContact(response);
      if (response && !customer?.boardName) {
        let boardName = `${response?.firstName || ''} ${response?.lastName || ''}`.trim();
        onChange({ ...customer, boardName });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getExistingUsers = async () => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LIST,
      requestBody: {
        filter: {
          email: customer?.email
        }
      }
    });
    if (error) {
      toast.error(error);
      return;
    }
    setExistingUsers(response);
  };

  useDebouncedEffect(
    () => {
      if (show && customer?.email && !crmContact) {
        getMatchingContactFromEmail(customer?.email);
        if (
          validateEmail(customer?.email) &&
          (mode === 'add' || (mode === 'edit' && parentCustomer?.email !== customer?.email))
        ) {
          getExistingUsers();
        } else {
          setExistingUsers([]);
        }
      } else {
        setMatchingCRMContact(null);
        setExistingUsers([]);
      }
    },
    [customer?.email, show, mode, crmContact],
    500
  );

  useEffect(() => {
    setMatchingCRMContact(null);
  }, [show]);

  const errorMessages = useMemo(() => {
    return existingUsers?.length > 0
      ? [
          {
            key: 'email',
            message: translate('email_already_in_use'),
            onClick: () => {
              setParentCustomer(existingUsers[0]);
              onHide();
            }
          }
        ]
      : [];
  }, [existingUsers]);

  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header className="d-flex justify-content-between" closeButton={!showProgress}>
        <Modal.Title className="flex-grow-1 flex items-center">
          <h6 className="mb-0 d-inline-block">
            {translate(mode === 'clone' ? 'clone_customer' : mode === 'edit' ? 'update_customer' : 'add_new_customer')}
          </h6>
          {mode === 'edit' && (
            <CustomerActions
              translate={translate}
              customerAction={[
                { onClick: onSendInvitationLinkToCustomerClick, text: 'send_invitation_link', Icon: EnvelopeOpen },
                { onClick: onInviteClick, text: 'Send_Password_Reset_Link', Icon: PersonPlus },
                { onClick: onCloneClick, text: 'clone', Icon: Clipboard },
                {
                  onClick: onShowAllStrategiesClick,
                  text: customer?.strategies?.every(s => s.showToCustomer)
                    ? 'hide_all_strategies_from_customer'
                    : 'show_all_strategies_from_customer',
                  Icon: customer?.strategies?.every(s => s.showToCustomer) ? EyeSlash : Eye
                }
              ]}
            />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto">
        <div>
          {/* board name */}
          <div className="d-flex flex-column gap-2 px-2">
            <h6 className="smallFont mb-0">{translate('board_name')}</h6>
            <FormControl
              size="sm"
              disabled={showProgress}
              value={customer?.boardName}
              onChange={e => onChange({ ...customer, boardName: e.target.value })}
            />
          </div>
          {Boolean(matchingCRMContact) && (
            <MatchingCRMContact
              contact={matchingCRMContact}
              onViewCRMContactClick={onViewCRMContactClick}
              translate={translate}
            />
          )}
          {crmContact && (
            <div className="border mt-2 mx-2">
              <div
                onClick={() => onViewCRMContactClick(crmContact)}
                className="hover px-2 d-flex align-items-center py-1 bg-primary-light border-bottom"
              >
                <PersonCircle /> <span className="smallFont mx-2 fw-bold">{translate('this_customer_is_linked')} </span>{' '}
                <BoxArrowUpRight size={12} />
              </div>
              <OverViewColumns
                parentContainerClassName="mid m-0 p-1 bg-light"
                inputCellStyle={{ fontSize: 14 }}
                cellSizeMD={12}
                data={crmContact}
                onDataChange={crmContact => {
                  onChange({ ...customer, crmContact });
                }}
                fields={editCrmContactDetailsField}
                isUpdating={showProgress}
                alwaysInEditMode={false}
                editable={true}
                errorMessages={errorMessages}
              />
            </div>
          )}
          <OverViewColumns
            parentContainerClassName="mid m-0 p-0"
            inputCellStyle={{ fontSize: 14 }}
            cellSizeMD={12}
            data={customer}
            onDataChange={onChange}
            fields={customerFormField}
            isUpdating={showProgress}
            alwaysInEditMode={false}
            editable={true}
            errorMessages={errorMessages}
          />
        </div>
        {showProgress && (
          <HorizontalProgress text={`${translate(mode === 'edit' ? 'updating_customer' : 'saving_customer')}...`} />
        )}
      </Modal.Body>
      <Modal.Footer className={mode === 'add' ? 'd-flex justify-content-between' : ''}>
        {mode === 'add' && (
          <Form.Check
            type="checkbox"
            reverse={isRTL}
            style={{
              fontSize: '14px'
            }}
            label={<h6 className="mb-0 smallFont">{translate('send_invitation_to_customer')}</h6>}
            checked={customer?.sendEmail}
            onChange={e => {
              onChange({ ...customer, sendEmail: e.target.checked });
            }}
          />
        )}
        <Button size="sm" variant="primary" disabled={showProgress} onClick={onCustomerSubmit}>
          {translate(mode === 'edit' ? 'update' : 'add')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditCustomerModal;
